import { create } from 'zustand';
import storage from '../native/storage.js';
import badgesData from '../data/badges.json';
import { STORAGE_KEYS, XP, SYNC_ENABLED } from '../data/constants.js';
import { appendSessionToOutbox, appendMiniGameToOutbox } from '../sync/emit.js';
import { ensureSyncFields } from '../sync/schemaV8.js';
import { applySm2, applyMistakeFlag } from '../utils/adaptive.js';
import { updateWeaknessProfile } from '../utils/errorPattern.js';
import { computeStreakUpdate, formatDateISO } from '../utils/dateUtils.js';
import { perfectSessionBonus, xpForAnswer } from '../utils/scoring.js';
import {
  buildDailyQuestSlice,
  applyQuestEvent,
  countWrongPool,
} from '../utils/dailyQuests.js';
import {
  archiveQuestSlice,
  populateArchiveForDates,
  synthesizeQuestSlice,
} from '../utils/dailyQuestArchive.js';
import { listMakeupSchoolDays } from '../utils/makeup.js';
import {
  countUnseenInGrade,
  clampImpossibleQuests,
} from '../utils/questAvailability.js';
import { addGrowth } from '../utils/petGrowth.js';
import { PET_REWARDS } from '../data/petStages.js';
import {
  applyTagDeltas,
  deltasFromOutcomes,
  deltasFromQuests,
} from '../utils/forestGrowth.js';
import { DEFAULT_AVATAR } from '../data/avatarCatalog.jsx';
import { sanitizeEquipped, isUnlocked } from '../utils/avatarUnlocks.js';
import {
  migrateProgressData,
  CURRENT_SCHEMA_VERSION,
  DEFAULT_EXERCISE_TYPE_STATS,
  EXERCISE_HISTORY_LIMIT,
  DEFAULT_SYNC_META,
} from '../utils/progressMigration.js';
import { isSchoolDay } from '../utils/dateUtils.js';

/**
 * Persistent progress state. Hydrated once on app boot via hydrateProgressStore().
 * Every state-mutating action triggers an async save.
 *
 * NOTE: We do not use zustand/middleware/persist because it expects a string-based
 * storage and our native abstraction works with parsed values. Custom subscribe
 * keeps the contract simple.
 */

const initialState = {
  hydrated: false,
  schemaVersion: CURRENT_SCHEMA_VERSION,
  xp: 0,
  currentStreak: 0,
  bestStreak: 0,
  lastSessionDate: null,
  badges: [], // array of badge IDs the child has earned
  totalSessions: 0,
  perfectSessions: 0,
  weaknessProfile: {}, // { tag: score }
  itemTracking: {}, // { wordId: trackingRecord }
  // Daily quests — { date: 'YYYY-MM-DD', quests: [...] }.
  // Re-rolled the first time any answer/session lands on a new date.
  dailyQuests: { date: null, quests: [] },
  // Pet growth — see utils/petGrowth.js
  pet: { growth: 0, stage: 0 },
  // Avatar customization — slot → item id
  avatar: { ...DEFAULT_AVATAR },
  // Calendar of days on which all daily quests completed — used by the
  // training-calendar UI and the 30-day-trained badge/hat rewards.
  // Stored as ISO date strings ('YYYY-MM-DD'), deduped, in insertion order.
  completedTrainingDays: [],
  // ── v3 extended tracking ──────────────────────────────────────────────
  bestCorrectStreak: 0,
  currentCorrectStreak: 0,
  reviewsCompleted: 0,
  dailyQuestsCompleted: 0,
  exerciseTypeStats: { ...DEFAULT_EXERCISE_TYPE_STATS },
  lastPlayedGrade: null,
  // ── v4 fields ──────────────────────────────────────────────────────────
  exerciseHistory: [],
  weeklyReviewDates: [],
  // ── v5 fields ──────────────────────────────────────────────────────────
  dailyQuestHistory: {},
  firstActiveDate: null,
  // ── v6 fields ──────────────────────────────────────────────────────────
  badgesEarnedAt: {},
  // ── v7 fields ──────────────────────────────────────────────────────────
  // Λεξοδάσος — per-tag growth map. See utils/forestGrowth.js for shape.
  forest: {},
  // ── v8 fields (sync layer — DORMANT, nothing offline reads these) ───────
  profileId: null,
  deviceId: null,
  syncMeta: { ...DEFAULT_SYNC_META },
  outbox: [],
  // Which child is currently playing on this device. NOT part of the persisted
  // progress blob (it is a pointer, saved separately under ACTIVE_PROFILE); it
  // selects WHICH blob to load/save. Null = the legacy single-profile key, so
  // the offline-only build behaves exactly as before.
  activeProfileId: null,
};

const PERSISTED_KEYS = [
  'schemaVersion',
  'xp',
  'currentStreak',
  'bestStreak',
  'lastSessionDate',
  'badges',
  'totalSessions',
  'perfectSessions',
  'weaknessProfile',
  'itemTracking',
  'dailyQuests',
  'pet',
  'avatar',
  'completedTrainingDays',
  'bestCorrectStreak',
  'currentCorrectStreak',
  'reviewsCompleted',
  'dailyQuestsCompleted',
  'exerciseTypeStats',
  'lastPlayedGrade',
  'exerciseHistory',
  'weeklyReviewDates',
  'dailyQuestHistory',
  'firstActiveDate',
  'badgesEarnedAt',
  'forest',
  'profileId',
  'deviceId',
  'syncMeta',
  'outbox',
];

function pickPersistable(state) {
  const out = {};
  for (const k of PERSISTED_KEYS) out[k] = state[k];
  return out;
}

/**
 * Storage key for a given active profile. A null/empty id maps to the legacy
 * single-profile key, so existing offline data keeps loading and the
 * offline-only build is byte-for-byte unchanged; a non-null id namespaces the
 * blob per child (siblings sharing one device).
 */
function progressKeyFor(activeProfileId) {
  return activeProfileId
    ? `${STORAGE_KEYS.PROGRESS}::${activeProfileId}`
    : STORAGE_KEYS.PROGRESS;
}

// Sync-transport bookkeeping fields. These are LOCAL-only (per-device identity
// + the upload queue + sync cursors) and must never travel inside the progress
// snapshot we reconcile with the server.
const SYNC_LOCAL_KEYS = ['profileId', 'deviceId', 'syncMeta', 'outbox'];

/**
 * The progress snapshot the sync layer reconciles with the server: every
 * persisted progress field EXCEPT the sync-transport bookkeeping. Pure +
 * side-effect free; safe to call even with the sync feature off (it is just a
 * projection and nothing reads it until the sync layer is switched on).
 */
export function selectSyncSnapshot(state) {
  const out = {};
  for (const k of PERSISTED_KEYS) {
    if (SYNC_LOCAL_KEYS.includes(k)) continue;
    out[k] = state[k];
  }
  return out;
}

/**
 * Ensure the daily quest slice matches `today`. Rolls a fresh set of quests
 * when the date changes. Pure — returns the next slice.
 *
 * Delegates to `buildDailyQuestSlice` for the day's composition:
 *   - Saturday / Sunday → no missions (rest day, empty slice).
 *   - Friday (with history) → only the weekly review mission.
 *   - Mon–Thu (or a history-less Friday) → the normal random set, with the
 *     dynamic `mistakes_cleared` quest clamped to the live wrong-pool count
 *     (zero pool → quest excluded so the child never sees an impossible task).
 */
/**
 * Wrapper around `rolloverDailyQuestsIfNeeded` that also archives the
 * previous-day slice when a rollover actually happens. Returns both the
 * new slice and the next archive map so the caller can persist both in
 * a single `set()` call. Use this instead of `rolloverDailyQuestsIfNeeded`
 * directly anywhere `state.dailyQuestHistory` lives nearby.
 */
function rolloverWithArchive(state, todayISO, context = {}) {
  const oldSlice = state.dailyQuests;
  const nextSlice = rolloverDailyQuestsIfNeeded(oldSlice, todayISO, context);
  // Same reference → no rollover happened; archive untouched.
  if (nextSlice === oldSlice) {
    return { slice: nextSlice, archive: state.dailyQuestHistory };
  }
  return {
    slice: nextSlice,
    archive: archiveQuestSlice(state.dailyQuestHistory, oldSlice),
  };
}

function rolloverDailyQuestsIfNeeded(slice, todayISO, context = {}) {
  if (slice && slice.date === todayISO && Array.isArray(slice.quests)) {
    return slice;
  }
  return buildDailyQuestSlice(todayISO, {
    availableWrongCount: countWrongPool(context.itemTracking),
    hasHistory: (context.exerciseHistoryCount ?? 0) > 0,
  });
}

/**
 * Push one or more quest events into the current daily slice. Returns:
 *   { slice, xpGained, completedCount }
 * where xpGained is the sum of `xpReward` for quests that JUST completed
 * during this batch (and therefore should be added to the running XP total)
 * and completedCount is how many distinct quests completed in this batch
 * — used to bump the cumulative `dailyQuestsCompleted` counter.
 */
function feedQuests(slice, events) {
  let next = slice;
  let xpGained = 0;
  let completedCount = 0;
  for (const ev of events) {
    const { quests, justCompleted } = applyQuestEvent(next.quests, ev);
    next = { ...next, quests };
    if (justCompleted.length === 0) continue;
    // Mark just-completed quests as rewardClaimed and sum their reward.
    let claimedThisRound = 0;
    next.quests = next.quests.map((q) => {
      if (!justCompleted.includes(q.id) || q.rewardClaimed) return q;
      claimedThisRound += q.xpReward ?? 0;
      completedCount += 1;
      return { ...q, rewardClaimed: true };
    });
    xpGained += claimedThisRound;
  }
  return { slice: next, xpGained, completedCount };
}

export const useProgressStore = create((set, get) => ({
  ...initialState,

  /**
   * Atomically commit the full outcome of a finished session.
   *
   * IMPORTANT: This is the ONLY way progress (XP, SM-2 tracking, weakness
   * profile, streak, daily-quest progress, pet, badges, training calendar)
   * advances. If a session is abandoned mid-way (close button, browser back,
   * tab close) no call to this method happens and the user keeps the
   * progress they had before the session started — by design.
   *
   * The caller is responsible for only invoking this once per fully-completed
   * session (after review for 'practice' mode, after practice for 'mistakes'
   * mode), with the full list of practice-phase outcomes.
   *
   * @returns {{
   *   totalXpGained: number, perAnswerXp: number, perfectBonus: number,
   *   dailyBonus: number, questXp: number, newBadges: string[],
   *   perfect: boolean, correctCount: number, totalCount: number,
   *   petStageChanged: boolean, completedTrainingDayAdded: string | null,
   * }}
   */
  commitSession({
    practiceOutcomes,
    mode = 'practice',
    hasReview = false,
    grade = null,
    now = new Date(),
    makeupTargetDate = null,
  }) {
    const state = get();
    const safeOutcomes = Array.isArray(practiceOutcomes) ? practiceOutcomes : [];

    // Empty sessions don't advance any progress. Defensive guard so a bug
    // (e.g. mistakes mode with empty pool) can't accidentally bump streak
    // or pet growth.
    if (safeOutcomes.length === 0) {
      return {
        totalXpGained: 0,
        perAnswerXp: 0,
        perfectBonus: 0,
        dailyBonus: 0,
        questXp: 0,
        newBadges: [],
        perfect: false,
        correctCount: 0,
        totalCount: 0,
        petStageChanged: false,
        completedTrainingDayAdded: null,
        questsCompletedDelta: 0,
        forestStageChanges: [],
      };
    }

    const todayISO = formatDateISO(now);

    // ── Per-outcome accumulators ───────────────────────────────────────
    const nextTrackingMutations = {};
    let nextWeakness = state.weaknessProfile;
    let perAnswerXp = 0;
    let petGrowthDelta = 0;
    const events = [];

    // Running correct-streak counter: walk outcomes in submission order
    // and reset on any wrong-on-first-attempt. The "best ever" is updated
    // alongside so the badge family can read it cheaply later.
    let runningCorrectStreak = state.currentCorrectStreak ?? 0;
    let bestCorrectStreak = state.bestCorrectStreak ?? 0;

    const exerciseTypeStats = { ...state.exerciseTypeStats };

    for (const outcome of safeOutcomes) {
      const {
        item,
        isCorrect,
        attemptNumber,
        errorTags = [],
        firstAttemptCorrect = null,
        exerciseType = null,
      } = outcome ?? {};
      if (!item || !item.id) continue;

      perAnswerXp += xpForAnswer({ isCorrect, attemptNumber });

      const previousRecord = state.itemTracking[item.id];
      const sm2Record = applySm2(
        previousRecord,
        isCorrect,
        isCorrect ? [] : errorTags,
        item.id,
        now
      );
      // Mistake-recap flag is derived from the FIRST attempt, not the final
      // outcome — see sessionStore for how this is captured.
      const nextTrackingRecord = applyMistakeFlag(
        sm2Record,
        firstAttemptCorrect
      );
      nextTrackingMutations[item.id] = nextTrackingRecord;

      // For weakness scoring we want the FULL tag set of the item, not just
      // the mistake tag the exercise reported. A correct answer should give
      // relief to every tag the item carries (that's the only way a focused
      // session can drop the % over time); a wrong answer reuses the same
      // tags so the score still rises on every related slip-up.
      const weaknessTags =
        isCorrect ? item.error_tags || [] : errorTags || item.error_tags || [];
      nextWeakness = updateWeaknessProfile(
        nextWeakness,
        weaknessTags,
        isCorrect
      );

      // Correct-streak bookkeeping.
      if (firstAttemptCorrect === true) {
        runningCorrectStreak += 1;
        if (runningCorrectStreak > bestCorrectStreak) {
          bestCorrectStreak = runningCorrectStreak;
        }
      } else {
        runningCorrectStreak = 0;
      }

      // Per-exercise-type tally: only on truly correct final answers, so
      // a child who muddled through and got it on attempt 3 still gets
      // some credit, but per-type diversity comes from real successes.
      if (isCorrect && exerciseType && exerciseType in exerciseTypeStats) {
        exerciseTypeStats[exerciseType] += 1;
      }

      // Quest events derived from this single outcome.
      const isFirstSighting = !previousRecord;
      if (isFirstSighting && isCorrect) {
        events.push({ type: 'new_words', count: 1 });
      }
      if (firstAttemptCorrect === true) {
        events.push({ type: 'correct_first', count: 1 });
      }
      const wasWrong = previousRecord?.wrongPool === true;
      const nowCleared = nextTrackingRecord.wrongPool === false;
      if (wasWrong && nowCleared) {
        events.push({ type: 'mistakes_cleared', count: 1 });
        petGrowthDelta += PET_REWARDS.ITEM_MASTERED;
      }
    }

    // ── Session-level summary ──────────────────────────────────────────
    const totalCount = safeOutcomes.length;
    const correctCount = safeOutcomes.filter((o) => o?.isCorrect).length;
    const perfect = totalCount > 0 && correctCount === totalCount;
    const bonus = perfectSessionBonus(correctCount, totalCount);

    // Streak (and daily login bonus, awarded on first session of a new day).
    const streakUpdate = computeStreakUpdate(
      state.currentStreak,
      state.lastSessionDate,
      now
    );
    const isNewDay = streakUpdate.lastSessionDate !== state.lastSessionDate;
    const dailyLoginBonus = isNewDay ? XP.DAILY_LOGIN : 0;

    // Session-level quest events.
    events.push({ type: 'sessions_played', count: 1 });
    if (perfect) events.push({ type: 'perfect_session', count: 1 });
    // History-review sessions tick off the weekly mandatory quest.
    if (mode === 'history') events.push({ type: 'history_review', count: 1 });

    const rollover = rolloverWithArchive(state, todayISO, {
      itemTracking: state.itemTracking,
      exerciseHistoryCount: state.exerciseHistory.length,
    });
    let dailyQuests = rollover.slice;
    let dailyQuestHistory = rollover.archive;
    const fed = feedQuests(dailyQuests, events);
    dailyQuests = fed.slice;
    const questXp = fed.xpGained;
    const questsCompletedDelta = fed.completedCount;

    // Auto-complete quests that have become physically impossible —
    // there aren't enough remaining unseen items in the child's actual
    // playing grade to satisfy what's left of the quest. Counting per-
    // grade (not across all grades) matters: a child who only plays
    // grade 3 won't tap into grade 2's untouched pool, even though
    // it's full. Without this clamp the day stays orange forever for
    // late-game children who've already exposed every word in their grade.
    const mergedTracking = {
      ...state.itemTracking,
      ...nextTrackingMutations,
    };
    const clampGrade =
      typeof grade === 'string' && grade.length > 0
        ? grade
        : state.lastPlayedGrade;
    // When we don't know which grade the child is in (e.g. a test path or
    // a callsite that forgot to pass grade), DON'T clamp — treating the
    // grade as "0 unseen" would auto-complete every new_words quest and
    // turn a partial session into a fully-completed training day. Use
    // `Infinity` here so `clampImpossibleQuests` becomes a no-op.
    const unseenAvailable = clampGrade
      ? countUnseenInGrade(clampGrade, mergedTracking)
      : Infinity;
    dailyQuests = clampImpossibleQuests(dailyQuests, unseenAvailable);

    // Make-up flow: when targeting a past missed day, the SAME events
    // also fire against that day's archived quest slice. The kid's
    // session work counts twice: once for today, once for the missed
    // day. When the missed day's slice fully completes, the date is
    // added to completedTrainingDays so the medals (which are pure-
    // computed) update automatically.
    //
    // Defensive: when the archive entry for the target date is somehow
    // missing (lazy-populate didn't fire, or a bug skipped it) we
    // synthesize one on the fly from the deterministic seed. This makes
    // the make-up flow resilient — the kid never taps a date and then
    // sees nothing change.
    let makeupCompletedDate = null;
    if (typeof makeupTargetDate === 'string') {
      let archivedSlice = dailyQuestHistory?.[makeupTargetDate];
      if (!archivedSlice || !Array.isArray(archivedSlice.quests)) {
        archivedSlice = synthesizeQuestSlice(makeupTargetDate, {
          availableWrongCount: countWrongPool(state.itemTracking),
          hasHistory: state.exerciseHistory.length > 0,
        });
      }
      if (Array.isArray(archivedSlice.quests) && archivedSlice.quests.length > 0) {
        const makeupFed = feedQuests(archivedSlice, events);
        // Same impossible-quest clamp as today's slice — a make-up day
        // shouldn't stay stuck on "5 new words" when the child has no
        // unseen items left anywhere.
        const clampedSlice = clampImpossibleQuests(
          makeupFed.slice,
          unseenAvailable
        );
        dailyQuestHistory = {
          ...dailyQuestHistory,
          [makeupTargetDate]: clampedSlice,
        };
        const allDone =
          clampedSlice.quests.length > 0 &&
          clampedSlice.quests.every((q) => q.completed);
        if (
          allDone &&
          !state.completedTrainingDays.includes(makeupTargetDate)
        ) {
          makeupCompletedDate = makeupTargetDate;
        }
      }
    }

    // Pet growth: per-item bumps + session completion + perfect bonus.
    petGrowthDelta +=
      PET_REWARDS.SESSION_COMPLETED +
      (perfect ? PET_REWARDS.PERFECT_SESSION_BONUS : 0);
    const petUpdate = addGrowth(state.pet, petGrowthDelta);

    // Λεξοδάσος (forest) per-tag growth. Two sources merged into a single
    // delta map: mastery+drip from the outcomes this commit covers, and
    // an even share of any quest reward the same commit earned. Quest
    // distribution only runs over tags the child has actually encountered
    // (either in the existing forest snapshot or in these very outcomes)
    // — see `deltasFromQuests` for the exact rule.
    const outcomeDeltas = deltasFromOutcomes(
      safeOutcomes,
      state.itemTracking,
      nextTrackingMutations
    );
    const recentTags = [];
    for (const o of safeOutcomes) {
      const tags = Array.isArray(o?.item?.error_tags) ? o.item.error_tags : [];
      for (const tag of tags) recentTags.push(tag);
    }
    const questDeltas = deltasFromQuests(
      state.forest,
      recentTags,
      questsCompletedDelta
    );
    const combinedForestDeltas = { ...outcomeDeltas };
    for (const [tag, n] of Object.entries(questDeltas)) {
      combinedForestDeltas[tag] = (combinedForestDeltas[tag] ?? 0) + n;
    }
    const forestUpdate = applyTagDeltas(state.forest, combinedForestDeltas);

    // Training calendar: if every daily quest is complete today AND today
    // isn't already in the list, mark today as a trained day. This is the
    // metric powering the 30-day-trained reward — separate from `streak`,
    // which requires consecutive days.
    let completedTrainingDays = state.completedTrainingDays;
    let completedTrainingDayAdded = null;
    const allQuestsComplete =
      dailyQuests.quests.length > 0 &&
      dailyQuests.quests.every((q) => q.completed);
    if (allQuestsComplete && !state.completedTrainingDays.includes(todayISO)) {
      completedTrainingDays = [...completedTrainingDays, todayISO];
      completedTrainingDayAdded = todayISO;
    }
    // If make-up just completed a past day, fold it into the same list so
    // the medals see it on the next render.
    if (
      makeupCompletedDate &&
      !completedTrainingDays.includes(makeupCompletedDate)
    ) {
      completedTrainingDays = [...completedTrainingDays, makeupCompletedDate];
    }

    const totalXpGained = perAnswerXp + bonus + dailyLoginBonus + questXp;
    const nextXp = state.xp + totalXpGained;
    const nextBest = Math.max(state.bestStreak, streakUpdate.streak);

    // Reviews are only counted on practice-mode sessions that actually
    // included the assessment phase. Mistakes-mode skips review by design.
    const reviewsCompleted =
      state.reviewsCompleted + (hasReview && mode === 'practice' ? 1 : 0);
    const nextLastPlayedGrade =
      typeof grade === 'string' && grade.length > 0
        ? grade
        : state.lastPlayedGrade;

    // Append this session's outcomes to the rolling exercise history,
    // capped at EXERCISE_HISTORY_LIMIT entries (oldest evicted first).
    // Drives the "Επανάληψη όλων" mode + the weekly mandatory review.
    const nowISO = now.toISOString();
    const historyAdditions = safeOutcomes
      .filter((o) => o?.item?.id)
      .map((o) => ({
        itemId: o.item.id,
        kind: 'session',
        mode,
        attemptedAt: nowISO,
        isCorrect: !!o.isCorrect,
        grade: typeof grade === 'string' ? grade : null,
      }));
    const nextExerciseHistory = [
      ...state.exerciseHistory,
      ...historyAdditions,
    ].slice(-EXERCISE_HISTORY_LIMIT);

    // If this was a history-review session AND today is a school day,
    // record today against the weekly mandatory-review log so the quest
    // doesn't re-fire later this Mon-Fri week.
    let nextWeeklyReviewDates = state.weeklyReviewDates;
    if (mode === 'history' && isSchoolDay(now) && !state.weeklyReviewDates.includes(todayISO)) {
      nextWeeklyReviewDates = [...state.weeklyReviewDates, todayISO];
    }

    // Stamp `firstActiveDate` once — never moves backwards thereafter.
    // Gates the calendar make-up window so brand-new accounts can't
    // see past-day misses they couldn't possibly have completed.
    const firstActiveDate = state.firstActiveDate ?? todayISO;

    set({
      xp: nextXp,
      currentStreak: streakUpdate.streak,
      bestStreak: nextBest,
      lastSessionDate: streakUpdate.lastSessionDate,
      totalSessions: state.totalSessions + 1,
      perfectSessions: state.perfectSessions + (perfect ? 1 : 0),
      weaknessProfile: nextWeakness,
      itemTracking: { ...state.itemTracking, ...nextTrackingMutations },
      dailyQuests,
      dailyQuestHistory,
      pet: petUpdate.state,
      completedTrainingDays,
      bestCorrectStreak,
      currentCorrectStreak: runningCorrectStreak,
      reviewsCompleted,
      dailyQuestsCompleted: state.dailyQuestsCompleted + questsCompletedDelta,
      exerciseTypeStats,
      lastPlayedGrade: nextLastPlayedGrade,
      exerciseHistory: nextExerciseHistory,
      weeklyReviewDates: nextWeeklyReviewDates,
      firstActiveDate,
      forest: forestUpdate.forest,
    });

    const newBadges = mintNewBadges(get, set, todayISO);

    const result = {
      totalXpGained,
      perAnswerXp,
      perfectBonus: bonus,
      dailyBonus: dailyLoginBonus,
      questXp,
      newBadges,
      perfect,
      correctCount,
      totalCount,
      petStageChanged: petUpdate.stageChanged,
      completedTrainingDayAdded,
      makeupCompletedDate,
      questsCompletedDelta,
      forestStageChanges: forestUpdate.stageChanges,
      // Indicates which session mode was committed; useful for the UI.
      mode,
    };
    // Sync layer (DORMANT unless SYNC_ENABLED): mirror the committed session
    // into the append-only outbox for later upload. No-op offline.
    //
    // Gated on baselineUploaded too: events are strictly POST-baseline deltas.
    // Pre-claim progress is captured ONCE by the baseline snapshot at claim
    // time; emitting events before then would double-count their XP (baseline
    // includes them AND the queued events would re-add them on the first push).
    if (SYNC_ENABLED && get().syncMeta?.baselineUploaded) {
      set({ outbox: appendSessionToOutbox(get(), { summary: result, mode, grade, now }) });
    }
    return result;
  },

  /**
   * Atomically commit the outcome of a finished MINI-GAME (the dictionary
   * Word↔Definition game or a standalone grammar-rule exercise set). These
   * games sit outside the normal 10-item session pipeline — they have a
   * fixed exercise count, single-attempt scoring, and no review phase — so
   * they need a parallel commit path that still awards XP, fires daily-
   * quest events, bumps streak / pet / badges, and increments totalSessions.
   *
   * Items aren't SM-2-tracked here (grammar-rule exercises and dictionary
   * rounds aren't in `itemTracking`), so this skips SM-2 / weaknessProfile
   * / wrongPool updates. Everything else mirrors `commitSession`.
   *
   * @param {object} args
   * @param {string} args.kind           'grammar_rule' | 'dictionary'
   * @param {number} args.correct        single-attempt correct answers
   * @param {number} args.total          total rounds played
   * @param {string|null} args.exerciseType  exercise type for the type-
   *        diversity stats (defaults to 'multipleChoice' since both games
   *        are MC under the hood).
   * @param {string|null} args.grade
   * @param {Date} args.now
   */
  commitMiniGame({
    kind = 'grammar_rule',
    correct = 0,
    total = 0,
    exerciseType = 'multipleChoice',
    grade = null,
    now = new Date(),
  } = {}) {
    const state = get();
    const safeCorrect = Math.max(0, Math.floor(correct));
    const safeTotal = Math.max(0, Math.floor(total));

    if (safeTotal === 0) {
      return {
        totalXpGained: 0,
        perAnswerXp: 0,
        perfectBonus: 0,
        dailyBonus: 0,
        questXp: 0,
        newBadges: [],
        perfect: false,
        correctCount: 0,
        totalCount: 0,
        petStageChanged: false,
        completedTrainingDayAdded: null,
        questsCompletedDelta: 0,
        forestStageChanges: [],
        kind,
      };
    }

    const todayISO = formatDateISO(now);
    const perfect = safeCorrect === safeTotal;

    // Single-attempt scoring: every correct answer counts as a first-
    // attempt correct, so it earns the full per-question XP.
    const perAnswerXp = safeCorrect * XP.CORRECT_FIRST_ATTEMPT;
    const bonus = perfect ? XP.PERFECT_SESSION_BONUS : 0;

    // Streak + daily-login.
    const streakUpdate = computeStreakUpdate(
      state.currentStreak,
      state.lastSessionDate,
      now
    );
    const isNewDay = streakUpdate.lastSessionDate !== state.lastSessionDate;
    const dailyLoginBonus = isNewDay ? XP.DAILY_LOGIN : 0;

    // Per-exercise-type tally. Both mini-games are MC under the hood so
    // they contribute to the `multipleChoice` bucket by default.
    const exerciseTypeStats = { ...state.exerciseTypeStats };
    if (exerciseType && exerciseType in exerciseTypeStats) {
      exerciseTypeStats[exerciseType] += safeCorrect;
    }

    // Daily-quest events: single attempt means every correct answer is a
    // "correct_first". A finished mini-game is a "sessions_played"; a
    // perfect run is a "perfect_session".
    const events = [];
    if (safeCorrect > 0) {
      events.push({ type: 'correct_first', count: safeCorrect });
    }
    events.push({ type: 'sessions_played', count: 1 });
    if (perfect) events.push({ type: 'perfect_session', count: 1 });

    const rollover = rolloverWithArchive(state, todayISO, {
      itemTracking: state.itemTracking,
      exerciseHistoryCount: state.exerciseHistory.length,
    });
    let dailyQuests = rollover.slice;
    const dailyQuestHistory = rollover.archive;
    const fed = feedQuests(dailyQuests, events);
    dailyQuests = fed.slice;
    const questXp = fed.xpGained;
    const questsCompletedDelta = fed.completedCount;

    // Pet growth — same SESSION_COMPLETED + perfect bonus as a real session.
    const petGrowthDelta =
      PET_REWARDS.SESSION_COMPLETED +
      (perfect ? PET_REWARDS.PERFECT_SESSION_BONUS : 0);
    const petUpdate = addGrowth(state.pet, petGrowthDelta);

    // Λεξοδάσος: mini-game items aren't error_tagged, so only quest
    // distribution kicks in here. The bonus is spread evenly across the
    // child's already-active forest tags (or none if the forest is empty).
    const forestUpdate = applyTagDeltas(
      state.forest,
      deltasFromQuests(state.forest, [], questsCompletedDelta)
    );

    // Training calendar (same rule as commitSession).
    let completedTrainingDays = state.completedTrainingDays;
    let completedTrainingDayAdded = null;
    const allQuestsComplete =
      dailyQuests.quests.length > 0 &&
      dailyQuests.quests.every((q) => q.completed);
    if (allQuestsComplete && !state.completedTrainingDays.includes(todayISO)) {
      completedTrainingDays = [...state.completedTrainingDays, todayISO];
      completedTrainingDayAdded = todayISO;
    }

    const totalXpGained = perAnswerXp + bonus + dailyLoginBonus + questXp;
    const nextBest = Math.max(state.bestStreak, streakUpdate.streak);

    // Correct-streak bookkeeping mirrors commitSession but is simpler here
    // since each correct is single-attempt.
    let runningCorrectStreak = state.currentCorrectStreak ?? 0;
    let bestCorrectStreak = state.bestCorrectStreak ?? 0;
    if (safeCorrect === safeTotal) {
      // Perfect run extends the streak by `safeCorrect`.
      runningCorrectStreak += safeCorrect;
      if (runningCorrectStreak > bestCorrectStreak) {
        bestCorrectStreak = runningCorrectStreak;
      }
    } else {
      // Any wrong answer in the run breaks the streak. Conservative: reset.
      runningCorrectStreak = 0;
    }

    const nextLastPlayedGrade =
      typeof grade === 'string' && grade.length > 0
        ? grade
        : state.lastPlayedGrade;
    const firstActiveDate = state.firstActiveDate ?? todayISO;

    set({
      xp: state.xp + totalXpGained,
      currentStreak: streakUpdate.streak,
      bestStreak: nextBest,
      lastSessionDate: streakUpdate.lastSessionDate,
      totalSessions: state.totalSessions + 1,
      perfectSessions: state.perfectSessions + (perfect ? 1 : 0),
      dailyQuests,
      dailyQuestHistory,
      pet: petUpdate.state,
      completedTrainingDays,
      bestCorrectStreak,
      currentCorrectStreak: runningCorrectStreak,
      dailyQuestsCompleted: state.dailyQuestsCompleted + questsCompletedDelta,
      exerciseTypeStats,
      lastPlayedGrade: nextLastPlayedGrade,
      firstActiveDate,
      forest: forestUpdate.forest,
    });

    const newBadges = mintNewBadges(get, set, todayISO);

    const result = {
      totalXpGained,
      perAnswerXp,
      perfectBonus: bonus,
      dailyBonus: dailyLoginBonus,
      questXp,
      newBadges,
      perfect,
      correctCount: safeCorrect,
      totalCount: safeTotal,
      petStageChanged: petUpdate.stageChanged,
      completedTrainingDayAdded,
      questsCompletedDelta,
      forestStageChanges: forestUpdate.stageChanges,
      kind,
    };
    // Sync layer (DORMANT unless SYNC_ENABLED): mirror the mini-game into the
    // outbox. No-op offline. Gated on baselineUploaded too — see commitSession
    // for why (pre-claim events would double-count against the baseline).
    if (SYNC_ENABLED && get().syncMeta?.baselineUploaded) {
      set({ outbox: appendMiniGameToOutbox(get(), { summary: result, kind, grade, now }) });
    }
    return result;
  },

  /**
   * Force the daily-quest slice to roll over when needed — useful when the
   * home page opens and we want today's quests visible before any answer.
   *
   * Always passes the live `itemTracking` snapshot through so dynamic quests
   * (e.g. `mistakes_cleared`) see the actual wrong-pool count.
   */
  ensureDailyQuests(now = new Date()) {
    const todayISO = formatDateISO(now);
    const state = get();
    const rollover = rolloverWithArchive(state, todayISO, {
      itemTracking: state.itemTracking,
      exerciseHistoryCount: state.exerciseHistory.length,
    });

    // Lazy-populate the make-up archive for past school days in the
    // eligibility window. Without this, days the child didn't open
    // the app on stay un-archived, and the calendar can't offer them
    // as tap-to-make-up — which is the common case for "I missed
    // Monday" since by definition they weren't around to populate.
    const makeupDates = listMakeupSchoolDays(state, now);
    const archiveAfterRollover = rollover.archive;
    let archive = populateArchiveForDates(
      archiveAfterRollover,
      makeupDates,
      {
        availableWrongCount: countWrongPool(state.itemTracking),
        hasHistory: state.exerciseHistory.length > 0,
      }
    );

    // Repair pass: clamp any "physically impossible" quests on archived
    // makeup slices using the child's CURRENT grade pool. Catches the
    // late-game scenario where a past day's archived quest (e.g. "Μάθε
    // 5 νέες λέξεις") can never finish because the grade's unseen pool
    // is depleted. Without this pass the day would stay orange forever
    // and the child would have to play yet another no-op session to
    // trigger the clamp at commit time.
    const playingGrade = state.lastPlayedGrade;
    let newlyCompletedDays = [];
    if (playingGrade) {
      const unseenInGrade = countUnseenInGrade(
        playingGrade,
        state.itemTracking
      );
      let clampMutated = false;
      let nextArchive = archive;
      for (const dateISO of makeupDates) {
        const slice = nextArchive[dateISO];
        if (!slice) continue;
        const clamped = clampImpossibleQuests(slice, unseenInGrade);
        if (clamped === slice) continue;
        if (!clampMutated) {
          nextArchive = { ...nextArchive };
          clampMutated = true;
        }
        nextArchive[dateISO] = clamped;
        const allDone =
          clamped.quests.length > 0 &&
          clamped.quests.every((q) => q.completed);
        if (allDone && !state.completedTrainingDays.includes(dateISO)) {
          newlyCompletedDays.push(dateISO);
        }
      }
      if (clampMutated) archive = nextArchive;
    }

    const sliceChanged = rollover.slice !== state.dailyQuests;
    const archiveChanged = archive !== state.dailyQuestHistory;
    if (sliceChanged || archiveChanged || newlyCompletedDays.length > 0) {
      const patch = {};
      if (sliceChanged) patch.dailyQuests = rollover.slice;
      if (archiveChanged) patch.dailyQuestHistory = archive;
      if (newlyCompletedDays.length > 0) {
        patch.completedTrainingDays = [
          ...state.completedTrainingDays,
          ...newlyCompletedDays,
        ];
      }
      set(patch);
    }
  },

  /**
   * Equip an avatar item. Rejected silently when the item isn't unlocked
   * at the child's current XP + badge state. Used by the avatar picker UI.
   */
  equipAvatar(slot, itemId) {
    const state = get();
    if (!isUnlocked(slot, itemId, state.xp, state.badges)) return false;
    set({ avatar: { ...state.avatar, [slot]: itemId } });
    return true;
  },

  /**
   * Pure read helper for consumers that need a sanitized equipped map
   * (defensive against legacy stored values that may no longer be unlocked).
   */
  selectAvatar() {
    const state = get();
    return sanitizeEquipped(state.avatar, state.xp, state.badges);
  },

  /**
   * Hard reset (used in settings or tests).
   */
  resetAll() {
    set({ ...initialState, hydrated: true });
  },
}));

/**
 * Hydrate from storage. Must be awaited before rendering the app.
 *
 * Runs the migration pipeline so older schemas keep working after an update,
 * and stores a one-shot backup of the previous value if migration changed
 * the shape — gives a recovery path if a migration step turns out to be wrong.
 */
export async function hydrateProgressStore() {
  // Which child's blob to load. Only consulted when SYNC_ENABLED (signed-in,
  // multi-profile); offline it stays null → the legacy single-profile key.
  let activeProfileId = null;
  if (SYNC_ENABLED) {
    try {
      const raw = await storage.getItem(STORAGE_KEYS.ACTIVE_PROFILE);
      if (typeof raw === 'string' && raw) activeProfileId = raw;
    } catch {
      activeProfileId = null;
    }
  }

  let saved = null;
  try {
    saved = await storage.getItem(progressKeyFor(activeProfileId));
  } catch {
    // Storage read failed; treat as no prior data. Fresh state.
    saved = null;
  }

  const { data, migrated, hadValidInput } = migrateProgressData(saved);
  // Sync layer (DORMANT unless SYNC_ENABLED): lazily stamp the pseudonymous
  // profileId/deviceId so the outbox/sync can attach. Kept OUT of the pure
  // migration; the setState below triggers the auto-persist subscription, so
  // the generated ids are saved. No-op (and no id generation) when offline.
  const hydrated = SYNC_ENABLED ? ensureSyncFields(data) : data;
  useProgressStore.setState({ ...hydrated, activeProfileId, hydrated: true });

  if (migrated && hadValidInput) {
    // Keep the previous parsed value under a backup key so we can recover
    // if a future build's migration step turns out to be wrong. Best-effort:
    // a failure here must not block hydration.
    try {
      await storage.setItem(STORAGE_KEYS.PROGRESS_BACKUP, saved);
    } catch {
      // ignored — backup is non-critical
    }
    // Persist the migrated shape immediately so subsequent boots skip the
    // migration entirely.
    try {
      await storage.setItem(
        progressKeyFor(activeProfileId),
        pickPersistable(useProgressStore.getState())
      );
    } catch {
      // ignored — auto-persist will retry on next state change
    }
  }
}

/**
 * Force-write the current state to ITS profile's key now, cancelling the
 * pending debounce. Used before switching profiles so the outgoing child's
 * progress can never be lost to an un-flushed debounce.
 */
export async function flushProgress() {
  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = null;
  }
  const state = useProgressStore.getState();
  if (!state.hydrated) return;
  try {
    await storage.setItem(progressKeyFor(state.activeProfileId), pickPersistable(state));
  } catch {
    // best-effort — auto-persist will retry on the next change
  }
}

/**
 * Switch the active child on this device: flush the current child's progress,
 * load the target child's local blob (or a fresh state bound to the server
 * profile), and activate it. Instant + offline-capable for children who have
 * already played here; a never-seen sibling starts empty and fills in from the
 * server on the next online sync (the caller triggers it via requestSync).
 *
 * @param {string} targetProfileId server learning-profile id
 * @param {{ grade?: number }} [opts] seed the grade for a brand-new local slot
 */
export async function switchProfile(targetProfileId, { grade } = {}) {
  if (!targetProfileId) return null;
  if (targetProfileId === useProgressStore.getState().activeProfileId) {
    return { profileId: targetProfileId, switched: false };
  }

  // 1. Persist the outgoing child to its own key.
  await flushProgress();

  // 2. Load the target child's blob (or a fresh state).
  let saved = null;
  try {
    saved = await storage.getItem(progressKeyFor(targetProfileId));
  } catch {
    saved = null;
  }
  const { data, hadValidInput } = migrateProgressData(saved);
  let next = SYNC_ENABLED ? ensureSyncFields(data) : data;

  // A child with no local data yet is bound to the server profile and marked
  // baseline-uploaded (the server is authoritative — we PULL, never re-baseline
  // an existing profile). A child WITH a local blob already carries its own ids.
  if (!hadValidInput) {
    next = {
      ...next,
      profileId: targetProfileId,
      syncMeta: { ...next.syncMeta, baselineUploaded: true },
      lastPlayedGrade: grade ?? next.lastPlayedGrade ?? null,
    };
  }

  // 3. Activate + persist the pointer + flush the target blob.
  useProgressStore.setState({ ...next, activeProfileId: targetProfileId, hydrated: true });
  try {
    await storage.setItem(STORAGE_KEYS.ACTIVE_PROFILE, targetProfileId);
  } catch {
    // best-effort
  }
  await flushProgress();

  return { profileId: targetProfileId, switched: true };
}

// Auto-persist on any state change, into the active child's namespaced key.
let persistTimer = null;
useProgressStore.subscribe((state) => {
  if (!state.hydrated) return;
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    storage.setItem(progressKeyFor(state.activeProfileId), pickPersistable(state));
  }, 150); // debounce rapid updates within one frame
});

/**
 * Evaluate badge conditions against the just-committed state, append any
 * newly-earned ones to `badges`, and stamp them in `badgesEarnedAt` with
 * `todayISO`. Returns the list of newly-earned IDs so the caller can show
 * a celebration toast etc. Safe to call after either commit path.
 */
function mintNewBadges(get, set, todayISO) {
  const newBadges = evaluateBadgesAfterUpdate(get());
  if (newBadges.length === 0) return newBadges;
  const cur = get();
  const nextEarnedAt = { ...(cur.badgesEarnedAt ?? {}) };
  for (const id of newBadges) {
    if (!nextEarnedAt[id]) nextEarnedAt[id] = todayISO;
  }
  set({
    badges: [...cur.badges, ...newBadges],
    badgesEarnedAt: nextEarnedAt,
  });
  return newBadges;
}

/**
 * Badge evaluation. New (not-yet-earned) badges only.
 */
function evaluateBadgesAfterUpdate(state) {
  const earned = new Set(state.badges);
  const newlyEarned = [];

  for (const badge of badgesData) {
    if (earned.has(badge.id)) continue;
    if (matchesCondition(badge.condition, state)) {
      newlyEarned.push(badge.id);
    }
  }
  return newlyEarned;
}

/**
 * Map of "fixed" badge conditions (no numeric parameter). The matcher
 * checks this first; anything that doesn't match a fixed key falls through
 * to the parametric matcher below.
 *
 * Adding a new fixed condition is safe — it only affects badges that
 * reference its key by id.
 */
const FIXED_CONDITIONS = {
  // ── Pre-Phase-2 conditions — DO NOT TOUCH. Existing badges depend on
  // these exact strings. New badges should use the parametric `*_gte_N`
  // form below where possible.
  sessions_gte_1: (s) => s.totalSessions >= 1,
  sessions_gte_10: (s) => s.totalSessions >= 10,
  streak_gte_3: (s) => s.currentStreak >= 3,
  streak_gte_7: (s) => s.currentStreak >= 7,
  streak_gte_14: (s) => s.currentStreak >= 14,
  streak_gte_30: (s) => s.currentStreak >= 30,
  perfect_sessions_gte_1: (s) => s.perfectSessions >= 1,
  perfect_sessions_gte_5: (s) => s.perfectSessions >= 5,
  xp_gte_100: (s) => s.xp >= 100,
  xp_gte_500: (s) => s.xp >= 500,
};

/**
 * Count how many tracked weakness tags the child has "improved" — meaning
 * the tag exists in the weakness profile (so they hit it at some point)
 * and its current score has dropped below 0.2.
 */
function countImprovedWeaknesses(state) {
  if (!state?.weaknessProfile || typeof state.weaknessProfile !== 'object') {
    return 0;
  }
  let n = 0;
  for (const score of Object.values(state.weaknessProfile)) {
    if (typeof score === 'number' && score < 0.2) n += 1;
  }
  return n;
}

/**
 * Count items the child has cleared — present in itemTracking AND not in
 * the wrong pool AND with at least one attempt recorded.
 */
function countMasteredItems(state) {
  if (!state?.itemTracking) return 0;
  let n = 0;
  for (const rec of Object.values(state.itemTracking)) {
    if (!rec) continue;
    const attempts = Number.isFinite(rec.attempts) ? rec.attempts : 0;
    if (attempts > 0 && rec.wrongPool === false) n += 1;
  }
  return n;
}

/**
 * Resolve a badge condition string against the current state. Supports
 * both fixed keys (registered in FIXED_CONDITIONS) and a small family of
 * parametric `<metric>_gte_<N>` patterns so we don't need a new code path
 * for every threshold.
 */
function matchesCondition(condition, state) {
  if (typeof condition !== 'string' || condition.length === 0) return false;

  const fixed = FIXED_CONDITIONS[condition];
  if (fixed) return fixed(state);

  // Parametric `<metric>_gte_<N>` form. Specific exercise-type keys carry
  // their own family because the metric name contains an underscore.
  const exerciseTypeMatch = condition.match(
    /^exercise_type_(fillTheBlank|dictation|multipleChoice|findTheMistake)_gte_(\d+)$/
  );
  if (exerciseTypeMatch) {
    const [, type, n] = exerciseTypeMatch;
    return (state.exerciseTypeStats?.[type] ?? 0) >= Number(n);
  }

  const genericMatch = condition.match(/^([a-z_]+)_gte_(\d+)$/);
  if (!genericMatch) return false;
  const [, metric, nStr] = genericMatch;
  const n = Number(nStr);

  switch (metric) {
    case 'xp':
      return state.xp >= n;
    case 'sessions':
      return state.totalSessions >= n;
    case 'streak':
      return state.currentStreak >= n;
    case 'perfect_sessions':
      return state.perfectSessions >= n;
    case 'correct_streak':
      return (state.bestCorrectStreak ?? 0) >= n;
    case 'reviews':
      return (state.reviewsCompleted ?? 0) >= n;
    case 'quests_completed':
      return (state.dailyQuestsCompleted ?? 0) >= n;
    case 'trained_days':
      return (state.completedTrainingDays?.length ?? 0) >= n;
    case 'weakness_improved':
      return countImprovedWeaknesses(state) >= n;
    case 'mastered_items':
      return countMasteredItems(state) >= n;
    case 'badges_earned':
      return (state.badges?.length ?? 0) >= n;
    default:
      return false;
  }
}

/**
 * Selector helpers
 */
export const selectXp = (s) => s.xp;
export const selectStreak = (s) => s.currentStreak;
export const selectBadges = (s) => s.badges;
export const selectWeaknessProfile = (s) => s.weaknessProfile;
export const selectDailyQuests = (s) => s.dailyQuests;
export const selectPet = (s) => s.pet ?? { growth: 0, stage: 0 };
export const selectAvatar = (s) => s.avatar ?? { hat: 'none' };
export const selectCompletedTrainingDays = (s) =>
  s.completedTrainingDays ?? [];
export const selectLastPlayedGrade = (s) => s.lastPlayedGrade ?? null;
export const selectActiveProfileId = (s) => s.activeProfileId ?? null;
export const selectBadgesEarnedAt = (s) => s.badgesEarnedAt ?? {};
export const selectForest = (s) => s.forest ?? {};

export { formatDateISO };
